import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/articles';

const hotArticles = articles.slice(0, 6);
const latestArticles = [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 6);

const faqData = [
  {
    q: '什么是无翼鸟漫画？',
    a: '无翼鸟漫画是一种独特的漫画创作风格，它在视觉表现上融合了日系漫画的精致感与独特的艺术表现手法，在叙事上打破传统线性结构，注重内在情感表达和思想深度。这种风格深受漫画爱好者喜爱，尤其在日系同人漫画圈中广受欢迎。',
  },
  {
    q: '在哪里可以阅读无翼鸟漫画？',
    a: '目前有多个在线平台提供无翼鸟漫画阅读服务。建议选择内容质量高、更新速度快、阅读体验好的平台。本站定期整理和评测各类漫画阅读平台，您可以关注我们的平台推荐文章获取最新信息。',
  },
  {
    q: '无翼鸟漫画适合什么年龄段的读者？',
    a: '无翼鸟漫画涵盖多种类型和题材。少年漫画和少女漫画适合全年龄段读者，而青年漫画则面向成年读者，可能包含较深的社会议题和复杂情节。建议读者根据具体作品的类型和题材选择适合自己年龄的作品。',
  },
  {
    q: '如何开始创作自己的无翼鸟风格漫画？',
    a: '创作漫画首先需要掌握基础的绘画技能和分镜设计能力。我们推荐从学习分镜技巧开始，然后逐步练习角色设计和故事创作。本站的漫画创作教程栏目提供了从零开始的详细指南，可以帮助你快速入门。',
  },
  {
    q: '无翼鸟漫画和传统日漫有什么区别？',
    a: '无翼鸟漫画在继承日漫优秀传统的基础上，更加注重个人化的艺术表达。视觉风格更加多元化，叙事方式更加自由，主题选择更偏向于探讨深层人性与情感议题。可以说，无翼鸟漫画是日漫文化在独立创作方向上的一种延伸与创新。',
  },
];

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '无翼鸟漫画',
  url: 'https://wingless-bird-manga.readdy.ai',
  description: '无翼鸟漫画资源导航与阅读指南，为您整理无翼鸟漫画的风格特点、阅读平台推荐、创作背景解析及相关资源导航。',
  inLanguage: 'zh-CN',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://wingless-bird-manga.readdy.ai/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '无翼鸟漫画',
  url: 'https://wingless-bird-manga.readdy.ai',
  description: '致力于为漫画爱好者提供优质的漫画资源导航、阅读推荐与创作指南。',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

export default function HomePage() {
  return (
    <>
      <SEOHead
        title="无翼鸟漫画-最新资源导航与阅读指南"
        description="无翼鸟漫画是深受漫画爱好者关注的创作类型，本页为您整理无翼鸟漫画的风格特点、阅读平台推荐、创作背景解析及相关资源导航，帮助漫画爱好者快速了解并找到优质内容。"
        keywords="无翼鸟漫画,无翼鸟漫画资源,无翼鸟漫画阅读,无翼鸟漫画网站,无翼鸟漫画推荐,日系同人漫画,漫画资源导航"
        canonicalUrl="/"
        structuredData={{ ...webSiteSchema, ...orgSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=artistic%20Japanese%20anime%20style%20illustration%20with%20soft%20gradient%20colors%2C%20floating%20manga%20panels%20and%20brush%20strokes%2C%20elegant%20composition%20on%20warm%20cream%20background%2C%20subtle%20golden%20accents%2C%20dreamy%20atmosphere&width=1920&height=1080&seq=hero-bg-2024&orientation=landscape"
              alt="无翼鸟漫画 - 漫画资源导航平台"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 md:mb-6 leading-tight">
              无翼鸟漫画
            </h1>
            <p className="text-base md:text-xl text-background-50/90 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed">
              发现优质漫画资源，探索精彩漫画世界。为您提供最全面的无翼鸟漫画推荐、阅读指南与创作教程。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/tongren"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-medium whitespace-nowrap hover:bg-primary-600 transition-colors text-sm"
              >
                浏览分类
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3 bg-background-50/15 text-background-50 border border-background-50/30 rounded-lg font-medium whitespace-nowrap hover:bg-background-50/25 transition-colors text-sm backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">漫画分类导航</h2>
              <p className="text-foreground-600 text-sm md:text-base">按类型浏览你感兴趣的漫画内容，发现更多精彩</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-500/30 transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground-950 mb-1 text-sm md:text-base whitespace-nowrap">{cat.name}</h3>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">热门推荐</h2>
                <p className="text-foreground-600 text-sm md:text-base">精选优质漫画文章，为你推荐最值得阅读的内容</p>
              </div>
              <Link to="/category/tongren" className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap font-medium">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {hotArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-500/20 transition-all duration-200"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 mb-2">
                        {cat && (
                          <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                            {cat.name}
                          </span>
                        )}
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                      </div>
                      <h3 className="font-semibold text-foreground-950 mb-2 line-clamp-2 text-sm md:text-base group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-200/70">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}
                        </span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          <i className="ri-user-line mr-1"></i>{article.author}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link to="/category/tongren" className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">最新更新</h2>
              <p className="text-foreground-600 text-sm md:text-base">实时更新最新的漫画资讯、教程与推荐文章</p>
            </div>
            <div className="max-w-3xl mx-auto">
              {latestArticles.map((article, index) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className={`flex items-start gap-4 py-4 group ${
                      index < latestArticles.length - 1 ? 'border-b border-background-200/70' : ''
                    }`}
                  >
                    <div className="w-20 h-14 md:w-24 md:h-16 flex-shrink-0 rounded-md overflow-hidden">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground-950 text-sm md:text-base group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {cat && (
                          <span className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                            {cat.name}
                          </span>
                        )}
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">常见问题</h2>
              <p className="text-foreground-600 text-sm md:text-base">关于无翼鸟漫画的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqData.map((item, index) => (
                <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="font-medium text-foreground-950 text-sm md:text-base pr-4">{item.q}</span>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-500 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">开始你的漫画探索之旅</h2>
            <p className="text-foreground-600 mb-8 text-sm md:text-base">加入无翼鸟漫画社区，发现更多精彩漫画资源与创作灵感</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/tongren"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-medium whitespace-nowrap hover:bg-primary-600 transition-colors text-sm"
              >
                立即浏览
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3 border border-background-200/70 text-foreground-700 rounded-lg font-medium whitespace-nowrap hover:bg-background-100 transition-colors text-sm"
              >
                关于我们
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}